import { useCallback, useEffect, useRef, useState } from 'react';
import {
  approach,
  cameraTarget,
  CONFIG,
  createGame,
  drop,
  getStats,
  makeDebris,
  step,
  stepDebris,
} from './logic';
import { drawScene, Phase, Scene, Viewport, viewHeightWorld } from './render';

export interface PancakeTowerResult {
  score: number;
  durationMs: number;
  stats: Record<string, number>;
}

export interface PancakeTowerProps {
  onGameStart?: () => void;
  onGameOver?: (r: PancakeTowerResult) => void;
}

/** Feel/tuning knobs for the presentation layer (game rules live in logic.CONFIG). */
export const FEEL = {
  /** pause after the miss so the last pancake visibly falls before the result card */
  gameOverDelayMs: 900,
  sparkles: 16,
  sparklesReduced: 4,
  trimShakePx: 3,
  missShakePx: 9,
  cameraRate: 6,
  maxDpr: 2,
};

const BEST_KEY = 'woxa.pancakeTower.best';

function readBest(): number {
  try {
    return Number(window.localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeBest(v: number) {
  try {
    window.localStorage.setItem(BEST_KEY, String(v));
  } catch {
    /* private mode: ignore */
  }
}

function newScene(): Scene {
  return {
    game: createGame(),
    debris: [],
    sparkles: [],
    popups: [],
    camera: 0,
    shake: 0,
    squash: 0,
    flash: 0,
    ghost: null,
    time: 0,
  };
}

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable;
}

const DROP_KEYS = new Set([' ', 'Spacebar', 'Enter', 'ArrowDown']);

interface Final {
  score: number;
  layers: number;
  perfects: number;
  bestStreak: number;
  newBest: boolean;
}

export default function PancakeTower({ onGameStart, onGameOver }: PancakeTowerProps) {
  const [phase, setPhase] = useState<Phase>('start');
  const [final, setFinal] = useState<Final | null>(null);
  const [best, setBest] = useState(readBest);

  const phaseRef = useRef<Phase>('start');
  const sceneRef = useRef<Scene>(newScene());
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const vpRef = useRef<Viewport>({ cssW: 0, cssH: 0, dpr: 1, scale: 0 });
  const reducedRef = useRef(false);
  const overTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onGameOverRef = useRef(onGameOver);
  const bestRef = useRef(best);

  useEffect(() => {
    onGameOverRef.current = onGameOver;
  }, [onGameOver]);

  const finish = useCallback(() => {
    overTimerRef.current = null;
    const g = sceneRef.current.game;
    const stats = getStats(g);
    const result: PancakeTowerResult = { score: g.score, durationMs: Math.round(g.elapsedMs), stats };
    const newBest = g.score > bestRef.current;
    if (newBest) {
      bestRef.current = g.score;
      writeBest(g.score);
      setBest(g.score);
    }
    phaseRef.current = 'over';
    setPhase('over');
    setFinal({ score: g.score, layers: stats.layers, perfects: stats.perfects, bestStreak: stats.bestStreak, newBest });
    onGameOverRef.current?.(result);
  }, []);

  const doDrop = useCallback(() => {
    if (phaseRef.current !== 'playing') return;
    const sc = sceneRef.current;
    const g = sc.game;
    if (g.over) return;
    const reduced = reducedRef.current;
    const row = g.stack.length;
    const slider = { ...g.current };
    const r = drop(g);
    const H = CONFIG.layerHeight;

    if (r.kind === 'perfect') {
      const top = g.stack[g.stack.length - 1];
      const n = reduced ? FEEL.sparklesReduced : FEEL.sparkles;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const sp = 60 + (i % 4) * 25;
        sc.sparkles.push({
          x: top.x + top.w / 2 + Math.cos(a) * top.w * 0.4,
          y: (row + 0.5) * H,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp * 0.6 + 40,
          life: 650,
          max: 650,
          size: 4 + (i % 3) * 2,
          color: i % 2 ? '#ffd27a' : '#fffaf0',
        });
      }
      sc.popups.push({
        text: g.streak >= 2 ? `เป๊ะ! ×${g.streak}` : 'เป๊ะ!',
        x: top.x + top.w / 2,
        y: (row + 3.5) * H, // above the slider + ghost
        life: 750,
        max: 750,
      });
      sc.flash = 1;
      sc.squash = 1;
    } else if (r.kind === 'trim' && r.cut) {
      sc.debris.push(makeDebris(r.cut, row, 'plain', reduced));
      sc.shake = reduced ? 0 : FEEL.trimShakePx;
      sc.squash = 1;
    } else if (r.kind === 'miss' && r.cut) {
      sc.debris.push(makeDebris(r.cut, row, 'plain', reduced));
      sc.ghost = { x: slider.x + slider.w / 2, y: (row + 1) * H };
      sc.shake = reduced ? 0 : FEEL.missShakePx;
      overTimerRef.current = setTimeout(finish, FEEL.gameOverDelayMs);
    }
    if (liveRef.current) liveRef.current.textContent = `คะแนน ${g.score}`;
  }, [finish]);

  const start = useCallback(() => {
    if (overTimerRef.current) clearTimeout(overTimerRef.current);
    overTimerRef.current = null;
    sceneRef.current = newScene();
    phaseRef.current = 'playing';
    setPhase('playing');
    setFinal(null);
    if (liveRef.current) liveRef.current.textContent = 'คะแนน 0';
    wrapRef.current?.focus({ preventScroll: true });
    onGameStart?.();
  }, [onGameStart]);

  // reduced-motion preference
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedRef.current = mq.matches;
    const onChange = (e: MediaQueryListEvent) => { reducedRef.current = e.matches; };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // canvas sizing
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const resize = () => {
      const cssW = wrap.clientWidth;
      const cssH = wrap.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, FEEL.maxDpr);
      vpRef.current = { cssW, cssH, dpr, scale: cssW / CONFIG.worldWidth };
      canvas.width = Math.max(1, Math.round(cssW * dpr));
      canvas.height = Math.max(1, Math.round(cssH * dpr));
    };
    resize();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(resize);
      ro.observe(wrap);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // the rAF loop: all per-frame state lives in refs, React is never touched here
  useEffect(() => {
    if (typeof window.requestAnimationFrame !== 'function') return;
    const ctx = canvasRef.current?.getContext('2d') ?? null;
    let raf = 0;
    let last: number | null = null;
    let running = false;

    const update = (dt: number) => {
      const sc = sceneRef.current;
      const vp = vpRef.current;
      const sec = dt / 1000;
      // the slider only moves while playing; debris/sparkles keep animating on the result card
      if (phaseRef.current === 'playing') step(sc.game, dt);
      for (const d of sc.debris) stepDebris(d, dt);
      if (sc.debris.length) sc.debris = sc.debris.filter((d) => d.y > sc.camera - BOTTOM_KEEP);
      for (const p of sc.sparkles) {
        p.life -= dt;
        p.x += p.vx * sec;
        p.y += p.vy * sec;
        p.vy -= 160 * sec;
      }
      if (sc.sparkles.length) sc.sparkles = sc.sparkles.filter((p) => p.life > 0);
      for (const p of sc.popups) p.life -= dt;
      if (sc.popups.length) sc.popups = sc.popups.filter((p) => p.life > 0);
      if (sc.ghost) sc.ghost.y += 40 * sec;
      if (vp.scale > 0) {
        const target = cameraTarget(sc.game.stack.length + 2, viewHeightWorld(vp));
        sc.camera = approach(sc.camera, target, dt, reducedRef.current ? FEEL.cameraRate * 2 : FEEL.cameraRate);
      }
      const decay = Math.exp(-sec * 10);
      sc.shake *= decay;
      sc.squash = Math.max(0, sc.squash - sec * 7);
      sc.flash = Math.max(0, sc.flash - sec * 2.2);
      sc.time += dt;
    };

    const frame = (t: number) => {
      if (!running) return;
      const dt = last == null ? 16 : Math.min(t - last, CONFIG.maxDtMs);
      last = t;
      update(dt);
      const vp = vpRef.current;
      if (ctx && vp.scale > 0) drawScene(ctx, sceneRef.current, vp, phaseRef.current, reducedRef.current);
      raf = window.requestAnimationFrame(frame);
    };
    const startLoop = () => {
      if (running) return;
      running = true;
      last = null;
      raf = window.requestAnimationFrame(frame);
    };
    const stopLoop = () => {
      running = false;
      window.cancelAnimationFrame(raf);
    };
    const onVis = () => (document.hidden ? stopLoop() : startLoop());
    document.addEventListener('visibilitychange', onVis);
    if (!document.hidden) startLoop();
    return () => {
      stopLoop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (phaseRef.current !== 'playing' || e.repeat || !DROP_KEYS.has(e.key)) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      doDrop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doDrop]);

  useEffect(() => () => {
    if (overTimerRef.current) clearTimeout(overTimerRef.current);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (phaseRef.current !== 'playing') return;
    e.preventDefault();
    doDrop();
  };
  const stop = (e: React.PointerEvent) => e.stopPropagation();

  return (
    <div
      ref={wrapRef}
      data-testid="pancake-play-area"
      tabIndex={0}
      role="application"
      aria-label="เกมตึกแพนเค้ก"
      onPointerDown={onPointerDown}
      className="relative mx-auto w-full max-w-[460px] select-none overflow-hidden rounded-[28px] bg-[#fff8ee] shadow-[0_10px_30px_rgba(59,42,32,0.12)] outline-none ring-1 ring-[#f1dcc2] focus-visible:ring-2 focus-visible:ring-[#df7836]"
      style={{ height: 'clamp(380px, 72vh, 640px)', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
    >
      <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 block h-full w-full" />
      <div ref={liveRef} aria-live="polite" className="sr-only" />

      {phase === 'start' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#fff8ee]/80 px-6 text-center" onPointerDown={stop}>
          <StackArt />
          <h2 className="text-2xl font-extrabold text-[#3b2a20]">ตึกแพนเค้ก</h2>
          <p className="max-w-[300px] text-[15px] leading-relaxed text-[#5a4234]">
            แตะจอหรือกด Space วางแพนเค้กให้ตรงชั้นล่าง วางเป๊ะได้โบนัส!
          </p>
          {best > 0 && <p className="text-sm font-semibold text-[#b94716]">สถิติดีที่สุด {best}</p>}
          <button
            type="button"
            onClick={start}
            className="mt-1 min-h-[52px] min-w-[160px] rounded-full bg-[#df7836] px-8 text-lg font-extrabold text-white shadow-[0_4px_0_#b94716] active:translate-y-[2px] active:shadow-[0_2px_0_#b94716] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3b2a20]"
          >
            เริ่มเล่น
          </button>
        </div>
      )}

      {phase === 'over' && final && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#fff8ee]/85 px-6 text-center" onPointerDown={stop}>
          <p className="text-lg font-extrabold text-[#b94716]">แพนเค้กหล่นแล้ว!</p>
          <p className="text-sm text-[#5a4234]">คะแนน</p>
          <p data-testid="pancake-final-score" className="text-6xl font-extrabold leading-none text-[#3b2a20]">{final.score}</p>
          <p className="max-w-[300px] text-sm leading-relaxed text-[#5a4234]">
            {final.layers} ชั้น · เป๊ะ {final.perfects} ครั้ง · ต่อเนื่องสูงสุด {final.bestStreak}
          </p>
          <p className="text-sm font-semibold text-[#b94716]">
            {final.newBest ? 'สถิติใหม่!' : `สถิติดีที่สุด ${best}`}
          </p>
          <button
            type="button"
            onClick={start}
            autoFocus
            className="mt-2 min-h-[52px] min-w-[180px] rounded-full bg-[#df7836] px-8 text-lg font-extrabold text-white shadow-[0_4px_0_#b94716] active:translate-y-[2px] active:shadow-[0_2px_0_#b94716] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3b2a20]"
          >
            เล่นอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/** debris is dropped once it is this far (world units) below the camera */
const BOTTOM_KEEP = 300;

/** Start-screen illustration: a little pancake stack with the ghost on top. Original art. */
function StackArt() {
  return (
    <svg viewBox="0 0 120 96" width="132" height="106" aria-hidden="true" focusable="false">
      <ellipse cx="60" cy="88" rx="50" ry="6" fill="#3b2a20" opacity=".12" />
      <ellipse cx="60" cy="82" rx="52" ry="8" fill="#e6d6bf" />
      <ellipse cx="60" cy="80" rx="48" ry="6" fill="#fffdf8" />
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(0 ${-i * 13})`}>
          <rect x={22 + i * 3} y="64" width={76 - i * 6} height="13" rx="6.5" fill={i === 1 ? '#b94716' : '#c47a35'} />
          <rect x={22 + i * 3} y="64" width={76 - i * 6} height="9.5" rx="4.75" fill={i === 1 ? '#df7836' : '#f1b762'} />
        </g>
      ))}
      <path d="M33 39 h54 v3 q0 3 -3 3 h-3 v8 q0 3 -3 0 v-8 h-30 v12 q0 3 -3 0 v-12 h-3 q-3 0 -3 -3z" fill="#7a3f17" />
      <path d="M48 38 V20 a12 12 0 0 1 24 0 V38 q-4 -4 -8 0 q-4 -4 -8 0 q-4 -4 -8 0z" fill="#fffaf0" stroke="#dcc7a8" strokeWidth="1.2" />
      <ellipse cx="55.5" cy="21" rx="1.6" ry="2.3" fill="#3b2a20" />
      <ellipse cx="64.5" cy="21" rx="1.6" ry="2.3" fill="#3b2a20" />
      <path d="M57.5 25.5 q2.5 2.5 5 0" fill="none" stroke="#3b2a20" strokeWidth="1.3" strokeLinecap="round" />
      <ellipse cx="52" cy="25" rx="2.4" ry="1.2" fill="#e6968a" opacity=".55" />
      <ellipse cx="68" cy="25" rx="2.4" ry="1.2" fill="#e6968a" opacity=".55" />
    </svg>
  );
}
